/**
 * Settings page — WhatsApp expense capture linking (quick-260711-tl2).
 *
 * States:
 * - Unlinked, no pending code: "Link WhatsApp" button.
 * - Unlinked, code issued: show code + bot number + instruction, poll link status
 *   until phone becomes non-null.
 * - Linked: show the linked number + an Unlink button.
 *
 * Write controls (Link WhatsApp, Unlink) are hidden during view-only admin
 * impersonation, mirroring categories.tsx.
 */
import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { signOut } from 'aws-amplify/auth';
import { Info, Loader2, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  useWhatsAppLinkQuery,
  useCreateLinkCodeMutation,
  useUnlinkMutation,
} from '@/features/whatsapp/whatsappApi';
import { useDeleteAccountMutation } from '@/features/account/accountApi';
import { useImpersonationStore } from '@/features/admin/impersonationStore';

export const Route = createFileRoute('/_app/settings')({
  component: SettingsPage,
});

/** Poll interval (ms) while a link code has been issued and is awaiting redemption. */
const POLL_INTERVAL_MS = 3500;

/** Format a millisecond duration as m:ss for the code-expiry countdown. */
function formatCountdown(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function WhatsAppLinkSection() {
  const isImpersonating = useImpersonationStore((s) => s.targetSub != null);
  const [awaitingLink, setAwaitingLink] = useState(false);
  const [issuedCode, setIssuedCode] = useState<{
    code: string;
    botNumber: string;
    expiresAt: string;
  } | null>(null);
  const [remainingMs, setRemainingMs] = useState<number | null>(null);

  const linkQuery = useWhatsAppLinkQuery({
    refetchInterval: awaitingLink ? POLL_INTERVAL_MS : false,
  });
  const createLinkCode = useCreateLinkCodeMutation();
  const unlink = useUnlinkMutation();

  const phone = linkQuery.data?.phone ?? null;

  // Once the poll observes a linked phone, stop polling and clear the pending-code UI.
  useEffect(() => {
    if (awaitingLink && phone) {
      setAwaitingLink(false);
      setIssuedCode(null);
    }
  }, [awaitingLink, phone]);

  // Countdown to the code's server-side expiry (15 min). Ticks every second while a code
  // is pending; on reaching 0 the code UI is cleared and polling stops — the backend
  // enforces the real expiry at redemption time, this mirrors it so the user is never
  // staring at a dead code.
  useEffect(() => {
    if (!issuedCode) {
      setRemainingMs(null);
      return;
    }
    const expiresAtMs = Date.parse(issuedCode.expiresAt);
    const tick = () => {
      const left = expiresAtMs - Date.now();
      if (left <= 0) {
        setIssuedCode(null);
        setAwaitingLink(false);
        setRemainingMs(null);
        toast.error('Your link code expired. Generate a new one to link WhatsApp.');
      } else {
        setRemainingMs(left);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [issuedCode]);

  async function handleLinkWhatsApp() {
    try {
      const result = await createLinkCode.mutateAsync();
      setIssuedCode({ code: result.code, botNumber: result.botNumber, expiresAt: result.expiresAt });
      setAwaitingLink(true);
    } catch {
      toast.error('Could not generate a link code. Please try again.');
    }
  }

  async function handleUnlink() {
    if (!window.confirm('Unlink WhatsApp from your Broke or Not account?')) return;
    try {
      await unlink.mutateAsync();
      setAwaitingLink(false);
      setIssuedCode(null);
      toast.success('WhatsApp unlinked.');
    } catch {
      toast.error('Could not unlink WhatsApp. Please try again.');
    }
  }

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <MessageCircle className="h-4 w-4 text-slate-400" />
          WhatsApp expense capture
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {linkQuery.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>
        ) : phone ? (
          <div className="space-y-3">
            <p className="text-sm text-slate-700">
              ✓ Linked <span className="font-medium">+{phone}</span>
            </p>
            <p className="text-sm text-slate-500">
              Send a message like <span className="font-mono">bread 250</span> to add expenses
              from WhatsApp.
            </p>
            {!isImpersonating && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => { void handleUnlink(); }}
                disabled={unlink.isPending}
              >
                {unlink.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Unlink'}
              </Button>
            )}
          </div>
        ) : issuedCode ? (
          <div className="space-y-3">
            <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3">
              <div className="flex items-baseline justify-between">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  Your code
                </p>
                {remainingMs !== null && (
                  <p
                    className={`text-xs font-medium tabular-nums ${
                      remainingMs < 60_000 ? 'text-amber-600' : 'text-slate-500'
                    }`}
                  >
                    Expires in {formatCountdown(remainingMs)}
                  </p>
                )}
              </div>
              <p className="mt-1 text-2xl font-bold tracking-widest text-slate-900">
                {issuedCode.code}
              </p>
            </div>
            <p className="text-sm text-slate-700">
              Send <span className="font-mono">{issuedCode.code}</span> to{' '}
              <a
                href={`https://wa.me/${issuedCode.botNumber.replace(/\D/g, '')}?text=${encodeURIComponent(issuedCode.code)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-blue-600 underline hover:text-blue-700"
              >
                {issuedCode.botNumber}
              </a>{' '}
              on WhatsApp.
            </p>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Waiting for you to send the code…
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-slate-500">
              Link your WhatsApp number to add expenses by sending a message like{' '}
              <span className="font-mono">bread 250</span>.
            </p>
            {!isImpersonating && (
              <Button
                type="button"
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => { void handleLinkWhatsApp(); }}
                disabled={createLinkCode.isPending}
              >
                {createLinkCode.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Link WhatsApp'
                )}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Danger-zone account deletion (STOR-01, quick-260719-pkg): a destructive button opens
 * a type-DELETE confirmation dialog wired to DELETE /account. On success the user is
 * signed out and returned to /login. Hidden entirely while impersonating so an admin
 * viewing another user can never trigger deletion.
 */
function DeleteAccountControl() {
  const isImpersonating = useImpersonationStore((s) => s.targetSub != null);
  const navigate = useNavigate();
  const deleteAccount = useDeleteAccountMutation();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  if (isImpersonating) return null;

  async function handleConfirmDelete() {
    try {
      await deleteAccount.mutateAsync();
      toast.success('Your account has been deleted.');
      await signOut();
      void navigate({ to: '/login' });
    } catch {
      toast.error('Could not delete your account. Please try again.');
      // Keep the dialog open on failure — do not sign out.
    }
  }

  return (
    <div>
      <Button
        type="button"
        size="sm"
        className="bg-red-600 hover:bg-red-700 text-white"
        onClick={() => {
          setConfirmText('');
          setOpen(true);
        }}
      >
        Delete account
      </Button>
      <p className="text-sm text-slate-500">
        Permanently erase your account and all associated data.
      </p>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete account</DialogTitle>
            <DialogDescription>
              This action is <span className="font-semibold">permanent</span> and erases all of
              your data — balances, entries, categories, and any linked WhatsApp number. This
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label htmlFor="confirm-delete" className="text-sm font-medium text-slate-700">
              Type <span className="font-mono font-semibold">DELETE</span> to confirm
            </label>
            <Input
              id="confirm-delete"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              autoComplete="off"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={confirmText !== 'DELETE' || deleteAccount.isPending}
              onClick={() => { void handleConfirmDelete(); }}
            >
              {deleteAccount.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Delete account'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AboutLegalSection() {
  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <Info className="h-4 w-4 text-slate-400" />
          About & Legal
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p>
          <a
            href="https://brokeornot.com/privacy/"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-blue-600 underline hover:text-blue-700"
          >
            Privacy Policy
          </a>
        </p>
        <p>
          <a
            href="https://brokeornot.com/terms/"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-blue-600 underline hover:text-blue-700"
          >
            Terms of Service
          </a>
        </p>
        <DeleteAccountControl />
      </CardContent>
    </Card>
  );
}

/**
 * WhatsApp expense capture is hidden from the UI for now, by request (2026-07-29).
 *
 * UI-ONLY: the API (`/whatsapp/link`), its Lambda, the webhook app and any existing
 * links are all untouched and keep working — a user who already linked their number
 * can still send messages. This only removes the entry point from Settings, so
 * nobody new can start a link and the half-built feature isn't advertised.
 *
 * To bring it back: flip this to `true`. Nothing else needs changing.
 */
const SHOW_WHATSAPP_SECTION = false;

function SettingsPage() {
  return (
    <div className="mx-auto max-w-[640px] px-4 py-8 sm:px-6 animate-page-in motion-reduce:animate-none">
      <h1 className="mb-6 text-2xl font-semibold tracking-tight text-slate-900">Settings</h1>

      <div className="space-y-6">
        {SHOW_WHATSAPP_SECTION && <WhatsAppLinkSection />}
        <AboutLegalSection />
      </div>
    </div>
  );
}
